import { PUBLIC_TRANSPORT_TUNING } from "../../config/publicTransport.ts";
import {
  VILNIUS_PUBLIC_TRANSPORT_NETWORK_SOURCE,
  publicTransportPatternById,
} from "../../world/publicTransportRoutes.ts";
import {
  PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION,
  type PublicTransportFleetState,
  type PublicTransportMount,
} from "./index.ts";

export const PUBLIC_TRANSPORT_SAVE_KEY = "vilnius.public-transport.v1";

interface PublicTransportSaveV1 {
  readonly schemaVersion: typeof PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION;
  readonly sourceArchiveSha256: string;
  readonly tick: number;
  readonly serviceTimeSeconds: number;
  readonly mountByPlayerId: Readonly<Record<string, PublicTransportMount>>;
}

export function serializePublicTransportFleet(
  state: Readonly<PublicTransportFleetState>,
): string {
  const save: PublicTransportSaveV1 = {
    schemaVersion: PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION,
    sourceArchiveSha256:
      VILNIUS_PUBLIC_TRANSPORT_NETWORK_SOURCE.archiveSha256,
    tick: state.tick,
    serviceTimeSeconds: state.serviceTimeSeconds,
    mountByPlayerId: state.mountByPlayerId,
  };
  return JSON.stringify(save);
}

export function parsePublicTransportFleet(
  value: string,
): PublicTransportFleetState {
  const parsed: unknown = JSON.parse(value);
  const record = asRecord(parsed, "Public-transport save");
  if (record.schemaVersion !== PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION) {
    throw new Error("Unsupported public-transport save schemaVersion");
  }
  if (
    record.sourceArchiveSha256 !==
      VILNIUS_PUBLIC_TRANSPORT_NETWORK_SOURCE.archiveSha256
  ) {
    throw new Error("Public-transport save uses a different JUDU snapshot");
  }
  if (!Number.isSafeInteger(record.tick) || Number(record.tick) < 0) {
    throw new Error("Public-transport save tick is invalid");
  }
  if (
    typeof record.serviceTimeSeconds !== "number" ||
    !Number.isFinite(record.serviceTimeSeconds) ||
    record.serviceTimeSeconds < 0
  ) {
    throw new Error("Public-transport save service clock is invalid");
  }
  const savedMounts = asRecord(
    record.mountByPlayerId,
    "Public-transport save mounts",
  );
  const mountByPlayerId: Record<string, PublicTransportMount> = {};
  for (const [playerId, rawMount] of Object.entries(savedMounts)) {
    if (playerId.length === 0) {
      throw new Error("Public-transport save player ID is invalid");
    }
    const mount = asRecord(rawMount, `Public-transport mount ${playerId}`);
    const scheduledDepartureSeconds = Number(mount.scheduledDepartureSeconds);
    const boardedStopIndex = Number(mount.boardedStopIndex);
    if (
      typeof mount.vehicleId !== "string" ||
      typeof mount.patternId !== "string" ||
      typeof mount.scheduledDepartureSeconds !== "number" ||
      !Number.isSafeInteger(scheduledDepartureSeconds) ||
      !Number.isSafeInteger(boardedStopIndex) ||
      typeof mount.alightRequested !== "boolean"
    ) {
      throw new Error(`Public-transport mount ${playerId} is malformed`);
    }
    const pattern = publicTransportPatternById(mount.patternId);
    if (
      pattern === null ||
      boardedStopIndex < 0 ||
      boardedStopIndex >= pattern.stops.length ||
      !isScheduledDeparture(pattern.departuresSeconds, scheduledDepartureSeconds) ||
      mount.vehicleId !==
        `${mount.patternId}:trip:${scheduledDepartureSeconds}`
    ) {
      throw new Error(`Public-transport mount ${playerId} is inconsistent`);
    }
    mountByPlayerId[playerId] = {
      vehicleId: mount.vehicleId,
      patternId: mount.patternId,
      scheduledDepartureSeconds,
      boardedStopIndex,
      alightRequested: mount.alightRequested,
    };
  }
  return {
    schemaVersion: PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION,
    tick: Number(record.tick),
    serviceTimeSeconds: record.serviceTimeSeconds,
    boostedVehicleId: null,
    mountByPlayerId,
  };
}

function isScheduledDeparture(
  departuresSeconds: readonly number[],
  absoluteDepartureSeconds: number,
): boolean {
  const daySeconds = PUBLIC_TRANSPORT_TUNING.serviceDaySeconds;
  return departuresSeconds.some((scheduledDepartureSeconds) =>
    Number.isInteger(
      (absoluteDepartureSeconds - scheduledDepartureSeconds) / daySeconds,
    )
  );
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}
